import { useState, useEffect, useCallback } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { imageContentTypeForFile } from '../../shared/utils/contentTypeForFile';
import { compressImageForUpload } from '../../shared/utils/compressImageForUpload';
import { API_URL } from '../../config/env';

const BASE_PATH = `${API_URL}/v1/admin/organization/media_library`;

export default function useMediaLibrary({ enabled = true } = {}) {
  const isMounted = useIsMounted();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchItems = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(BASE_PATH);
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to load media library');
      setItems([]);
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  }, [enabled, isMounted]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // `variant: 'thumbnail'` asks for the small JPEG. The API falls back to the original when a
  // thumbnail does not exist yet and queues one, so the grid never waits and never shows holes.
  const fetchDownloadUrl = useCallback(async (mediaId, { variant } = {}) => {
    if (!mediaId) return null;
    const query = variant ? `?variant=${encodeURIComponent(variant)}` : '';
    const res = await fetchWithAuth(`${BASE_PATH}/${mediaId}/download${query}`);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.errors?.join(', ') || body.error || res.statusText);
    }
    const data = await res.json();
    return data.url;
  }, []);

  const fetchUsages = useCallback(async (mediaId) => {
    if (!mediaId) return [];
    const res = await fetchWithAuth(`${BASE_PATH}/${mediaId}/usages`);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.errors?.join(', ') || body.error || res.statusText);
    }
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  }, []);

  const uploadFile = useCallback(async (file, { compress = true } = {}) => {
    if (!file) return null;

    let uploadFile = file;
    if (compress) {
      uploadFile = await compressImageForUpload(file);
    }

    const contentType = imageContentTypeForFile(uploadFile);
    if (!contentType) {
      const err = new Error('Unsupported image type');
      setError(err.message);
      throw err;
    }

    setUploading(true);
    setError(null);
    let pendingMediaId = null;
    try {
      const prepareRes = await fetchWithAuth(BASE_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          media: {
            filename: uploadFile.name,
            content_type: contentType,
            byte_size: uploadFile.size,
          },
        }),
      });
      if (!prepareRes.ok) {
        const body = await prepareRes.json().catch(() => ({}));
        throw new Error(body.errors?.join(', ') || body.error || prepareRes.statusText);
      }
      const { media, upload } = await prepareRes.json();
      pendingMediaId = media.id;

      const putHeaders = { ...(upload.headers || {}), 'Content-Type': contentType };
      const putRes = await fetch(upload.url, {
        method: upload.method || 'PUT',
        body: uploadFile,
        headers: putHeaders,
      });
      if (!putRes.ok) {
        throw new Error('Upload to storage failed');
      }

      const completeRes = await fetchWithAuth(`${BASE_PATH}/${media.id}`, { method: 'PATCH' });
      if (!completeRes.ok) {
        const body = await completeRes.json().catch(() => ({}));
        throw new Error(body.errors?.join(', ') || body.error || completeRes.statusText);
      }

      const completeBody = await completeRes.json();
      await fetchItems();
      return {
        media: completeBody.media,
        deduplicated: Boolean(completeBody.deduplicated),
      };
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      if (pendingMediaId) {
        try {
          await fetchWithAuth(`${BASE_PATH}/${pendingMediaId}`, { method: 'DELETE' });
        } catch (cleanupErr) {
          if (!isMounted.current) return;
          console.error('Failed to remove incomplete upload record:', cleanupErr);
        }
      }
      await fetchItems();
      setError(err.message || 'Upload failed');
      throw err;
    } finally {
      if (!isMounted.current) return;
      setUploading(false);
    }
  }, [fetchItems, isMounted]);

  const deleteItem = useCallback(async (mediaId) => {
    if (!mediaId) return;
    setError(null);
    try {
      const res = await fetchWithAuth(`${BASE_PATH}/${mediaId}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.errors?.join(', ') || body.error || res.statusText);
      }
      await fetchItems();
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Delete failed');
      throw err;
    }
  }, [fetchItems, isMounted]);

  const readyItems = items.filter((item) => item.status === 'ready');

  return {
    items,
    readyItems,
    loading,
    error,
    uploading,
    refresh: fetchItems,
    uploadFile,
    deleteItem,
    fetchDownloadUrl,
    fetchUsages,
  };
}
